import { useEffect, useState, useRef } from "react";
import { FileText, Upload, Loader2, ClipboardEdit, Check, Trash2, Eye, Sparkles, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { relativeTime } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const SUBTESTS = [
  { key: "word_reading", label: "Word Reading" },
  { key: "sentence_comprehension", label: "Sentence Comprehension" },
  { key: "reading_composite", label: "Reading Composite" },
  { key: "spelling", label: "Spelling" },
  { key: "math_computation", label: "Math Computation" },
];

// Clone the score→proficiency formula from the backend so the confirm modal can preview updates live.
function ssToProficiency(ss) {
  if (ss === null || ss === undefined || ss === "" || Number.isNaN(Number(ss))) return null;
  const p = Math.round((Number(ss) - 70) * (100 / 60));
  return Math.max(0, Math.min(100, p));
}

function computeProposedUpdates(scores) {
  const out = {};
  const math = ssToProficiency(scores.math_computation);
  if (math !== null) out.Math = math;

  let reading = null;
  if (scores.reading_composite !== null && scores.reading_composite !== "") {
    reading = ssToProficiency(scores.reading_composite);
  } else {
    const wr = ssToProficiency(scores.word_reading);
    const sc = ssToProficiency(scores.sentence_comprehension);
    const pieces = [wr, sc].filter((x) => x !== null);
    if (pieces.length) reading = Math.round(pieces.reduce((a, b) => a + b, 0) / pieces.length);
  }
  if (reading !== null) out.Reading = reading;

  const writing = ssToProficiency(scores.spelling);
  if (writing !== null) out.Writing = writing;

  return out;
}

const emptyScores = {
  word_reading: "",
  sentence_comprehension: "",
  reading_composite: "",
  spelling: "",
  math_computation: "",
  date_of_test: "",
};

export default function AssessmentsPanel({ studentId, onAppliedUpdates }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewAssessment, setReviewAssessment] = useState(null);
  const [reviewScores, setReviewScores] = useState(emptyScores);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ ...emptyScores, notes: "" });
  const [applying, setApplying] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/assessments`);
      setItems(data);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId]);

  const openReview = (assessment) => {
    const p = assessment.parsed || {};
    const seeded = { ...emptyScores };
    for (const st of SUBTESTS) seeded[st.key] = p[st.key] ?? "";
    seeded.date_of_test = p.date_of_test || "";
    setReviewAssessment(assessment);
    setReviewScores(seeded);
    setReviewOpen(true);
  };

  const onFileSelected = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/students/${studentId}/assessments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      if (data.parse_error) {
        toast.warning("Upload saved, but score parsing couldn't read them — edit manually below.");
      } else {
        toast.success("Uploaded — review the parsed scores");
      }
      openReview(data.assessment);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const applyReview = async () => {
    if (!reviewAssessment) return;
    const proposed = computeProposedUpdates(reviewScores);
    if (Object.keys(proposed).length === 0) {
      toast.error("Enter at least one Standard Score");
      return;
    }
    setApplying(true);
    try {
      // Coerce empty strings to null before sending
      const scores = {};
      for (const [k, v] of Object.entries(reviewScores)) {
        if (k === "date_of_test") { scores[k] = v || null; continue; }
        scores[k] = (v === "" || v === null || v === undefined) ? null : Number(v);
      }
      const { data } = await api.post(`/students/${studentId}/assessments/${reviewAssessment.id}/apply`, { scores });
      toast.success(`Applied — updated ${Object.keys(data.applied_updates).length} subject(s)`);
      setReviewOpen(false);
      await load();
      onAppliedUpdates?.(data.applied_updates);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  const saveManual = async () => {
    const proposed = computeProposedUpdates(manualForm);
    if (Object.keys(proposed).length === 0) {
      toast.error("Enter at least one Standard Score");
      return;
    }
    try {
      // Convert empty strings → null before POST
      const payload = { notes: manualForm.notes || "" };
      for (const st of SUBTESTS) payload[st.key] = manualForm[st.key] === "" ? null : Number(manualForm[st.key]);
      payload.date_of_test = manualForm.date_of_test || null;
      const { data } = await api.post(`/students/${studentId}/assessments/manual`, payload);
      // Immediately apply the manual scores
      await api.post(`/students/${studentId}/assessments/${data.assessment.id}/apply`, { scores: null });
      toast.success("Manual scores saved and applied");
      setManualOpen(false);
      setManualForm({ ...emptyScores, notes: "" });
      await load();
      onAppliedUpdates?.(computeProposedUpdates(manualForm));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteItem = async (a) => {
    if (!window.confirm("Delete this assessment record? (Applied proficiency scores will remain.)")) return;
    try {
      await api.delete(`/students/${studentId}/assessments/${a.id}`);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openFile = async (a) => {
    if (!a.storage_path) return;
    try {
      const res = await api.get(`/students/${studentId}/assessments/${a.id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const proposed = computeProposedUpdates(reviewScores);

  return (
    <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="assessments-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" /> WRAT-5 Assessments
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            Upload a WRAT-5 report — we read it, extract Standard Scores, and (after your review) update the student's Math / Reading / Writing proficiencies.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => onFileSelected(e.target.files?.[0])}
            data-testid="assessment-file-input"
          />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="upload-assessment-btn"
          >
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Upload WRAT-5</>}
          </Button>
          <Button variant="outline" onClick={() => setManualOpen(true)} className="rounded-full border-2 border-stone-200" data-testid="manual-assessment-btn">
            <ClipboardEdit className="w-4 h-4 mr-2" /> Enter manually
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-500" data-testid="assessments-empty">
          No assessments yet. Upload a WRAT-5 PDF/image or enter scores manually.
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((a) => {
            const parsed = a.parsed || {};
            const applied = a.applied_updates || {};
            return (
              <div key={a.id} className="rounded-2xl border border-stone-200 p-4 flex items-center gap-4 flex-wrap" data-testid={`assessment-row-${a.id}`}>
                <div className={`w-10 h-10 rounded-xl grid place-items-center ${a.source === "manual" ? "bg-stone-100 text-stone-600" : "bg-emerald-50 text-emerald-700"}`}>
                  {a.source === "manual" ? <ClipboardEdit className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium truncate">{a.original_filename || "Manual entry"}</div>
                    <Badge className="rounded-full bg-stone-100 text-stone-700 text-[10px] uppercase tracking-[0.15em]">{a.source}</Badge>
                    {parsed.confidence && parsed.confidence !== "manual" && (
                      <Badge className={`rounded-full text-[10px] uppercase tracking-[0.15em] ${parsed.confidence === "high" ? "bg-emerald-100 text-emerald-700" : parsed.confidence === "low" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {parsed.confidence}% confidence
                      </Badge>
                    )}
                    {a.applied_at ? (
                      <Badge className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-[0.15em]">
                        <Check className="w-3 h-3 mr-1 inline" /> Applied
                      </Badge>
                    ) : a.parse_error ? (
                      <Badge className="rounded-full bg-red-100 text-red-700 text-[10px] uppercase tracking-[0.15em]" title={a.parse_error}>
                        <AlertTriangle className="w-3 h-3 mr-1 inline" /> Parse error
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {relativeTime(a.created_at)}
                    {Object.keys(applied).length > 0 && (
                      <> · applied → {Object.entries(applied).map(([k, v]) => `${k} ${v}`).join(", ")}</>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {a.storage_path && (
                    <Button size="sm" variant="ghost" onClick={() => openFile(a)} className="rounded-full text-stone-600" data-testid={`view-assessment-${a.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openReview(a)} className="rounded-full text-amber-700 hover:bg-amber-50" data-testid={`review-assessment-${a.id}`}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Review & apply
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteItem(a)} className="rounded-full text-red-600 hover:bg-red-50" data-testid={`delete-assessment-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Review dialog (upload path) */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl rounded-2xl" data-testid="review-assessment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Review WRAT-5 Standard Scores</DialogTitle>
            <DialogDescription>
              Edit any extracted value that looks wrong. When you click Apply, the corresponding HomeSchool Hub proficiencies (Math, Reading, Writing) will be updated using SS→proficiency mapping (SS 70→0, SS 100→50, SS 130→100).
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            {SUBTESTS.map((st) => (
              <div key={st.key}>
                <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">{st.label}</Label>
                <Input
                  type="number"
                  min={40}
                  max={160}
                  value={reviewScores[st.key]}
                  onChange={(e) => setReviewScores({ ...reviewScores, [st.key]: e.target.value })}
                  className="mt-2 rounded-xl h-11"
                  placeholder="SS 40–160"
                  data-testid={`review-input-${st.key}`}
                />
              </div>
            ))}
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">Date of test</Label>
              <Input
                type="date"
                value={reviewScores.date_of_test}
                onChange={(e) => setReviewScores({ ...reviewScores, date_of_test: e.target.value })}
                className="mt-2 rounded-xl h-11"
                data-testid="review-input-date"
              />
            </div>
          </div>

          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="review-preview">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-emerald-800">Preview: proficiency updates</div>
            {Object.keys(proposed).length === 0 ? (
              <div className="mt-2 text-sm text-emerald-800/70">Enter at least one Standard Score to see the derived proficiencies.</div>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(proposed).map(([subject, val]) => (
                  <span key={subject} className="rounded-full bg-white border border-emerald-300 text-emerald-800 px-3 py-1 text-sm font-medium">
                    {subject}: <strong>{val}</strong>/100
                  </span>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={applyReview}
              disabled={applying || Object.keys(proposed).length === 0}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="review-apply-btn"
            >
              {applying ? "Applying…" : <><Check className="w-4 h-4 mr-2" /> Apply to profile</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual entry dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-2xl rounded-2xl" data-testid="manual-assessment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Enter WRAT-5 scores manually</DialogTitle>
            <DialogDescription>
              Type each Standard Score (40–160) from the printed report. Scores immediately update the student's proficiencies when you save.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            {SUBTESTS.map((st) => (
              <div key={st.key}>
                <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">{st.label}</Label>
                <Input
                  type="number"
                  min={40}
                  max={160}
                  value={manualForm[st.key]}
                  onChange={(e) => setManualForm({ ...manualForm, [st.key]: e.target.value })}
                  className="mt-2 rounded-xl h-11"
                  placeholder="SS 40–160"
                  data-testid={`manual-input-${st.key}`}
                />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">Date of test</Label>
              <Input type="date" value={manualForm.date_of_test} onChange={(e) => setManualForm({ ...manualForm, date_of_test: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="manual-input-date" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">Notes (optional)</Label>
              <Textarea value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} className="mt-2 rounded-xl min-h-[70px]" data-testid="manual-input-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={saveManual}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="manual-save-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Save & apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
